import { VISITOR_ID_PATTERN, VISITOR_STORAGE_KEY } from './constants.js';
import { createVisitorId } from './create-visitor-id.js';
import type { VisitorComputePath, VisitorIdResolution } from './types.js';
import {
  createStoredVisitor,
  readVisitorFromStorage,
  writeVisitorToStorage,
  type VisitorSource,
} from './visitor-storage.js';

let inFlight: Promise<VisitorIdResolution> | null = null;
let memoryVisitor: ReturnType<typeof createStoredVisitor> | null = null;

/**
 * Resolves visitor id with metadata (cache vs compute path).
 * Use in tests / debug; production code can call `getVisitorId()`.
 */
export async function resolveVisitorId(): Promise<VisitorIdResolution> {
  const cached = readVisitorFromStorage();
  if (cached) {
    memoryVisitor = cached;
    return {
      visitorId: cached.visitorId,
      fromCache: true,
      source: cached.source,
    };
  }

  if (memoryVisitor) {
    return {
      visitorId: memoryVisitor.visitorId,
      fromCache: true,
      source: memoryVisitor.source,
    };
  }

  if (!inFlight) {
    inFlight = createAndPersistVisitorId().finally(() => {
      inFlight = null;
    });
  }

  return inFlight;
}

/**
 * Stable visitor identifier. Reads localStorage first and creates UUID v7 once.
 */
export async function getVisitorId(): Promise<string> {
  const result = await resolveVisitorId();
  return result.visitorId;
}

/** Clears persisted visitor id (tests / support). */
export function clearVisitorId(): void {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(VISITOR_STORAGE_KEY);
  }
  inFlight = null;
  memoryVisitor = null;
}

export function getStoredVisitor() {
  return readVisitorFromStorage();
}

async function createAndPersistVisitorId(): Promise<VisitorIdResolution> {
  const visitorId = createVisitorId();
  const source: VisitorSource = 'uuid-v7';
  const computePath: VisitorComputePath = 'uuid-v7';

  if (!VISITOR_ID_PATTERN.test(visitorId)) {
    throw new Error('[Teo Widget] Invalid visitor id format');
  }

  const stored = createStoredVisitor(visitorId, source);
  memoryVisitor = stored;
  writeVisitorToStorage(stored);

  console.info('[Teo Widget] Visitor ID: created', {
    visitorId,
    source,
    computePath,
  });

  return {
    visitorId,
    fromCache: false,
    source,
    computePath,
  };
}
